// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {DataLoader, Space, Styled} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {useState} from '@supermove/hooks';
import {OrganizationModel, UserModel} from '@supermove/models';

// App
import SalespersonCalendarSelect from '@shared/modules/Project/components/SalespersonCalendarSelect';
import SalespersonCalendarView from '@shared/modules/Project/components/SalespersonCalendarView';

const LoadingIndicator = Styled.Loading`
`;

const Container = Styled.View`
  flex: 1;
`;

const SalespersonCalendarContent = ({
  organization,
  jobDate,
  initialSelectedSalesPersonIds,
  calendarsPerPage,
}: {
  organization: OrganizationModel;
  jobDate?: moment.Moment;
  initialSelectedSalesPersonIds?: number[];
  calendarsPerPage: number;
}) => {
  const userIdToCalendarIdMap = organization.allCalendarUsers.reduce(
    (acc, calendarUser) => {
      acc[_.toNumber(calendarUser.user.id)] = _.toString(calendarUser.user.primaryCalendar.id);
      return acc;
    },
    {} as Record<number, string>,
  );

  const calendarUserIdToUserMap = organization.allCalendarUsers.reduce(
    (acc, calendarUser) => {
      acc[_.toNumber(calendarUser.user.id)] = calendarUser.user;
      return acc;
    },
    {} as Record<number, UserModel>,
  );

  const defaultSelectedSalesPersonIds = Array.from(
    new Set(
      initialSelectedSalesPersonIds ||
        organization.allCalendarUsers.map((calendarUser) => _.toNumber(calendarUser.user.id)),
    ),
  );

  const [selectedSalesPersonIds, setSelectedSalesPersonIds] = useState<number[]>(
    defaultSelectedSalesPersonIds,
  );

  const selectedUserWithCalendarId = selectedSalesPersonIds
    .filter((userId) => !!calendarUserIdToUserMap[userId])
    .map((userId) => ({
      user: calendarUserIdToUserMap[userId],
      calendarId: userIdToCalendarIdMap[userId],
    }));

  return (
    <Container>
      <SalespersonCalendarSelect
        selectedSalesPersonIds={selectedSalesPersonIds}
        organization={organization}
        handleSalesPersonIdsChange={setSelectedSalesPersonIds}
      />
      <Space height={16} />
      <SalespersonCalendarView
        selectedUserWithCalendarId={selectedUserWithCalendarId}
        jobDate={jobDate}
        calendarsPerPage={calendarsPerPage}
      />
    </Container>
  );
};

const SalespersonCalendar = ({
  initialSelectedSalesPersonIds,
  organizationSlug,
  jobDate,
  calendarsPerPage,
}: {
  initialSelectedSalesPersonIds?: number[];
  organizationSlug: string;
  jobDate?: moment.Moment;
  calendarsPerPage: number;
}) => {
  const {data, loading} = useQuery<{organization: OrganizationModel}>(SalespersonCalendar.query, {
    variables: {slug: organizationSlug},
  });

  return (
    <DataLoader data={data} loading={loading} as={LoadingIndicator}>
      {({loadedData}) => (
        <SalespersonCalendarContent
          organization={loadedData.organization}
          jobDate={jobDate}
          initialSelectedSalesPersonIds={initialSelectedSalesPersonIds}
          calendarsPerPage={calendarsPerPage}
        />
      )}
    </DataLoader>
  );
};

SalespersonCalendar.query = gql`
  ${SalespersonCalendarSelect.fragment}
  query SalespersonCalendar($slug: String!) {
    organization(slug: $slug) {
      id
      allCalendarUsers {
        id
        user {
          id
          email
          fullName
          primaryCalendar {
            id
          }
        }
      }
      ...SalespersonCalendarSelect
    }
  }
`;

export default SalespersonCalendar;
